import { $isCodeNode, CodeNode } from "@lexical/code-core";
import { mergeRegister } from "@lexical/utils";
import {
  $getNodeByKey,
  $getRoot,
  defineExtension,
  type LexicalExtension,
  type NodeKey,
} from "lexical";
import type { CodePainter, createCodePainter, readCode } from "../../renderer/code/paint";

/** What the dynamic import below brings. */
interface PaintModule {
  createCodePainter: typeof createCodePainter;
  readCode: typeof readCode;
}

/**
 * Syntax colors for code blocks. This only reads the editor: colors are ranges registered with the
 * browser over the text Lexical rendered, so there are no nodes of ours in the document, nothing
 * extra in what is stored, and no update made in answer to an update.
 *
 * What is in every editor is the two listeners below. The painter and the tokenizer are fetched
 * when an editor first has a code block to show. Painting waits for the next frame, so a burst of
 * keystrokes in a long block tokenizes it once, not once per key.
 */
export const CodeHighlightExtension: LexicalExtension<
  Record<never, never>,
  "z-editor/code-highlight",
  unknown,
  unknown
> = defineExtension({
  name: "z-editor/code-highlight",
  register: (editor) => {
    let module: PaintModule | null = null;
    let loading = false;
    let painter: CodePainter | null = null;
    let disposed = false;
    // Blocks to paint on the next frame, and the frame that will, in the page the editor is in.
    const pending = new Set<NodeKey>();
    let frame: { view: Window; id: number } | null = null;

    const codeBlocks = (): NodeKey[] =>
      editor.getEditorState().read(() =>
        $getRoot()
          .getChildren()
          .filter($isCodeNode)
          .map((node) => node.getKey()),
      );

    /** A painter belongs to the page the editor is shown in. */
    const attach = (): void => {
      painter?.dispose();
      const view = editor.getRootElement()?.ownerDocument.defaultView ?? null;
      painter = module === null || view === null ? null : module.createCodePainter(view);
      schedule(codeBlocks());
    };

    /** Asks for `keys` to be painted on the next frame. Without a page, they wait for one. */
    const schedule = (keys: Iterable<NodeKey>): void => {
      for (const key of keys) pending.add(key);
      if (frame !== null || pending.size === 0 || disposed) return;
      const view = editor.getRootElement()?.ownerDocument.defaultView ?? null;
      if (view === null) return;
      frame = {
        view,
        id: view.requestAnimationFrame(() => {
          frame = null;
          const due = [...pending];
          pending.clear();
          paint(due);
        }),
      };
    };

    /** Paints the blocks among `keys` that are code, in one read of the state. */
    const paint = (keys: Iterable<NodeKey>): void => {
      const gone: NodeKey[] = [];
      const blocks = editor.getEditorState().read(() => {
        const found: [NodeKey, string | null][] = [];
        for (const key of keys) {
          const node = $getNodeByKey(key);
          if ($isCodeNode(node)) found.push([key, node.getLanguage() ?? null]);
          // Not code, or no longer there: whatever was painted under this key goes.
          else gone.push(key);
        }
        return found;
      });
      if (blocks.length > 0 && module === null && !loading) {
        loading = true;
        import("./painter").then(
          (loaded) => {
            module = loaded;
            if (!disposed) attach();
          },
          // A chunk that failed to load (offline, or gone after a deploy): code stays readable,
          // uncolored, and the next block that needs painting asks for it again.
          () => {
            loading = false;
          },
        );
      }
      if (painter === null || module === null) return;
      const { readCode } = module;
      for (const key of gone) painter.clear(key);
      for (const [key, language] of blocks) {
        painter.paint(
          key,
          () => {
            const element = editor.getElementByKey(key);
            return element === null ? null : readCode([element]);
          },
          language,
        );
      }
    };

    // Both listeners below run for one commit, mutations first; a block is painted once.
    const done = new Set<NodeKey>();
    return mergeRegister(
      // Created and destroyed, undo and a replaced document included: the reconciler reports them
      // here, after the DOM is up to date.
      editor.registerMutationListener(
        CodeNode,
        (mutations) => {
          for (const key of mutations.keys()) done.add(key);
          schedule(mutations.keys());
        },
        { skipInitialization: true },
      ),
      // Typing changes the block's children, not the block, which no mutation reports; but every
      // ancestor of a changed node is among the dirty elements.
      editor.registerUpdateListener(({ dirtyElements }) => {
        schedule([...dirtyElements.keys()].filter((key) => !done.has(key)));
        done.clear();
      }),
      editor.registerRootListener(attach),
      () => {
        disposed = true;
        if (frame !== null) frame.view.cancelAnimationFrame(frame.id);
        frame = null;
        pending.clear();
        painter?.dispose();
      },
    );
  },
});
